import React, { useRef, useState } from "react";
import { Camera, Upload, Download, Trash2, Loader2, X, ImageOff } from "lucide-react";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import { addStopPhotos, deleteStopPhoto, downloadPodPhoto, stopPhotos } from "../lib/deliveries";

// Reusable Proof-of-Delivery photo manager for a single stop.
// Supports: Take Photo (camera), Upload from Device (library, multiple),
// preview before save, multiple photos, view / download / delete.
export default function PodPhotos({ job, stop, user, canEdit = true }) {
  const { toast } = useToast();
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);
  const [pending, setPending] = useState([]); // [{ file, url }]
  const [caption, setCaption] = useState("");
  const [capturedAt, setCapturedAt] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [viewer, setViewer] = useState(null);

  const photos = stopPhotos(stop);
  const delivered = stop.status === "delivered";
  const sn = stop.stop_number;

  const onSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setPending(files.map((f) => ({ file: f, url: URL.createObjectURL(f) })));
  };

  const save = async () => {
    setSaving(true);
    try {
      await addStopPhotos({ job, stop, files: pending.map((p) => p.file), uploader: user, caption: caption.trim(), capturedAt: capturedAt.trim() });
      toast({ title: "Proof of delivery saved", description: `${pending.length} photo(s) attached to ${stop.package_reference}.` });
      pending.forEach((p) => URL.revokeObjectURL(p.url));
      setPending([]); setCaption(""); setCapturedAt("");
    } catch (err) {
      toast({ title: "Upload failed", description: err?.message || "Check Firebase Storage rules." });
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    const p = confirmDel; setConfirmDel(null);
    try { await deleteStopPhoto({ job, stop, photoId: p.id, actor: user }); toast({ title: "Photo removed" }); }
    catch (err) { toast({ title: "Couldn't remove", description: err?.message }); }
  };

  const cancel = () => { pending.forEach((p) => URL.revokeObjectURL(p.url)); setPending([]); setCaption(""); setCapturedAt(""); };

  return (
    <div data-testid={`pod-photos-${sn}`}>
      {/* Existing photos */}
      {photos.length > 0 ? (
        <>
          <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-600 mb-2" data-testid={`pod-attached-${sn}`}>
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" /> POD photo attached ({photos.length})
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-3">
            {photos.map((p, i) => (
              <div key={p.id || i} className="relative group rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
                <img src={p.url} alt={`POD ${i + 1}`} className="w-full h-20 object-cover cursor-pointer" onClick={() => setViewer(p.url)} data-testid={`pod-thumb-${sn}-${i}`} />
                {(p.caption || p.captured_at) && <div className="px-1.5 py-1 text-[9px] leading-tight text-slate-500 truncate">{[p.caption, p.captured_at].filter(Boolean).join(" · ")}</div>}
                <div className="absolute inset-x-0 top-0 flex justify-center gap-1 p-1 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => downloadPodPhoto(stop, p.url, i, photos.length)} title="Download" className="text-white hover:text-[#f59321]" data-testid={`pod-download-${sn}-${i}`}><Download className="w-3.5 h-3.5" /></button>
                  {canEdit && <button onClick={() => setConfirmDel(p)} title="Delete" className="text-white hover:text-red-400" data-testid={`pod-delete-${sn}-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        delivered && (
          <div className="flex items-center gap-1.5 text-xs font-bold text-red-500 mb-2" data-testid={`pod-missing-${sn}`}>
            <ImageOff className="w-4 h-4" /> POD photo missing
          </div>
        )
      )}

      {/* Add buttons */}
      {canEdit && (
        <div className="grid grid-cols-2 gap-2">
          <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onSelect} data-testid={`pod-camera-input-${sn}`} />
          <input ref={libraryRef} type="file" accept="image/*,.heic,.heif,.jpg,.jpeg,.png" multiple className="hidden" onChange={onSelect} data-testid={`pod-upload-input-${sn}`} />
          <Button type="button" onClick={() => cameraRef.current?.click()} className="h-11 rounded-xl bg-[#0f2847] text-white text-sm font-bold" data-testid={`pod-take-photo-${sn}`}>
            <Camera className="w-4 h-4 mr-1.5" /> Take photo
          </Button>
          <Button type="button" onClick={() => libraryRef.current?.click()} variant="outline" className="h-11 rounded-xl border-2 text-sm font-bold" data-testid={`pod-upload-device-${sn}`}>
            <Upload className="w-4 h-4 mr-1.5" /> {photos.length ? "Add another" : "Upload from device"}
          </Button>
        </div>
      )}

      {/* Preview modal */}
      {pending.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid={`pod-preview-modal-${sn}`}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="text-xs font-bold text-[#f59321]">{stop.package_reference}</div>
                <div className="font-display font-extrabold text-[#0f2847]">{stop.recipient_name}</div>
                {stop.company_name && <div className="text-xs text-slate-600 font-semibold">{stop.company_name}</div>}
                <div className="text-xs text-slate-500">{stop.address_line_1}, {stop.city}, {stop.state} {stop.zip_code}</div>
              </div>
              <button onClick={cancel} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wide mb-1.5">Selected photo{pending.length > 1 ? "s" : ""}</div>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {pending.map((p, i) => <img key={i} src={p.url} className="w-full h-40 object-cover rounded-lg border border-slate-200" alt={`preview ${i + 1}`} />)}
            </div>
            <div className="space-y-2 mb-4">
              <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Delivery address / description (optional)" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" data-testid={`pod-caption-${sn}`} />
              <input value={capturedAt} onChange={(e) => setCapturedAt(e.target.value)} placeholder="Delivery time (optional), e.g. 10:42 AM" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" data-testid={`pod-capturedat-${sn}`} />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={cancel} className="flex-1 rounded-full h-11 font-semibold border-2" data-testid={`pod-preview-cancel-${sn}`}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="flex-1 rounded-full h-11 font-bold bg-[#f59321] hover:bg-[#e07f10] text-white" data-testid={`pod-preview-save-${sn}`}>
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save as proof of delivery"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {confirmDel && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 text-center">
            <Trash2 className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <div className="font-display font-extrabold text-[#0f2847]">Delete this POD photo?</div>
            <p className="text-sm text-slate-500 mt-1">This removes it from {stop.package_reference}. This cannot be undone.</p>
            <div className="flex gap-2 mt-4">
              <Button variant="outline" onClick={() => setConfirmDel(null)} className="flex-1 rounded-full h-10 font-semibold border-2">Cancel</Button>
              <Button onClick={doDelete} className="flex-1 rounded-full h-10 font-bold bg-red-500 hover:bg-red-600 text-white" data-testid={`pod-delete-confirm-${sn}`}>Delete</Button>
            </div>
          </div>
        </div>
      )}

      {/* Fullscreen viewer */}
      {viewer && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setViewer(null)} data-testid={`pod-viewer-${sn}`}>
          <img src={viewer} alt="POD full" className="max-w-full max-h-full rounded-lg" />
          <button className="absolute top-4 right-4 text-white"><X className="w-7 h-7" /></button>
        </div>
      )}
    </div>
  );
}
